import type { WindowUsageData } from '../../features/BusinessApps/types'

/**
 * Merges WindowUsageData arrays by grouping entries with the same id
 * and combining their metrics appropriately.
 *
 * @param dataArrays Array of WindowUsageData arrays to merge
 * @returns Merged WindowUsageData array with combined metrics
 */
export const mergeWindowUsageData = (
  dataArrays: (WindowUsageData[] | undefined)[],
): WindowUsageData[] => {
  // Filter out undefined arrays and flatten
  const allData = dataArrays.filter((data): data is WindowUsageData[] => Boolean(data)).flat()

  if (allData.length === 0) return []

  // Group by id
  const groupedById = allData.reduce(
    (acc, item) => {
      if (!acc[item.id]) {
        acc[item.id] = []
      }
      acc[item.id].push(item)
      return acc
    },
    {} as Record<string, WindowUsageData[]>,
  )

  // Merge each group
  const mergedData = Object.values(groupedById).map((group) => {
    if (group.length === 1) {
      return group[0]
    }

    // Use the first item as template (id, name, tags should be same for all)
    const template = group[0]

    // Collect all unique teams
    const allTeams = new Set<string>()
    group.forEach((item) => {
      item.teams.forEach((team) => allTeams.add(team))
    })

    // Sum the metrics
    const combinedMetrics = group.reduce(
      (acc, item) => ({
        visits: acc.visits + item.visits,
        time_ms: acc.time_ms + item.time_ms,
        time_h: acc.time_h + item.time_h,
        annual_cost: acc.annual_cost + item.annual_cost,
      }),
      { visits: 0, time_ms: 0, time_h: 0, annual_cost: 0 },
    )

    return {
      ...template,
      ...combinedMetrics,
      teams: Array.from(allTeams),
      team_count: allTeams.size,
    }
  })

  // Sort by time_h in descending order (largest first)
  return mergedData.sort((a, b) => b.time_h - a.time_h)
}

